import React, { useState } from 'react';
import arcaLogo from '../assets/arca_logo.png';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for navigation
import PublicLayout from '../components/public/PublicLayout';

const AuthForms = ({ onAuthSuccess }) => {
  // State to manage the current view: 'login', 'passwordReset', or 'loggedIn'
  const [currentView, setCurrentView] = useState('login');
  const navigate = useNavigate(); // Initialize useNavigate hook

  // Callback function for successful authentication
  const handleAuthSuccess = (userData) => {
    if (onAuthSuccess) {
      onAuthSuccess(userData); // Call the parent's auth success handler
    }
  };

  // Callback function for switching to membership (signup)
  const handleSwitchToMembership = () => {
    navigate('/membership_registration'); // Navigate to the membership registration page
  };



  // Function to switch to the password reset view
  const switchToPasswordReset = () => {
    setCurrentView('passwordReset');
  };

  // Function to switch back to the login view
  const switchToLogin = () => {
    setCurrentView('login');
  };

  return (
    <PublicLayout>
      <div className="py-5" style={{ backgroundColor: '#ffffff' }}>
        <div className="container d-flex align-items-center justify-content-center" style={{ minHeight: '500px' }}>
          {currentView === 'passwordReset' ? (
            // Display the password reset form
            <PasswordResetForm onBackToLogin={switchToLogin} />
          ) : (
            // Display the login form by default
            <LoginForm // Now renders the specific LoginForm component
              onAuthSuccess={handleAuthSuccess}
              onSwitchToMembership={handleSwitchToMembership}
              onForgotPassword={switchToPasswordReset} // Pass the new callback for "Forgot password?"
            />
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

// Component specifically for the Login Form
const LoginForm = ({ onAuthSuccess, onSwitchToMembership, onForgotPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Try actual API calls (no more demo mode)
    try {
      // First try admin login with email - FIXED ENDPOINT
      const adminResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password
        })
      });

      if (adminResponse.ok) {
        const adminData = await adminResponse.json();
        console.log('✅ Admin login successful:', adminData);

        // Store admin token
        const token = adminData.data.token;
        localStorage.setItem('adminToken', token);

        // Create userData with token included
        const userData = {
          id: adminData.data.admin.id,
          username: adminData.data.admin.username,
          email: adminData.data.admin.email,
          firstName: adminData.data.admin.firstName,
          lastName: adminData.data.admin.lastName,
          userType: adminData.data.admin.role || 'admin',
          token: token  // Include token in userData
        };

        console.log('📦 Passing userData with token to onAuthSuccess:', userData);
        onAuthSuccess(userData);
        setIsSubmitting(false);
        return;
      }

      // If admin login fails, try regular user login - FIXED ENDPOINT
      const userResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password
        })
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        console.log('✅ User login successful:', userData);

        // Store the access token
        const token = userData.data.accessToken;
        localStorage.setItem('userToken', token);

        // Create user data object with token included
        const userInfo = {
          id: userData.data.user.id,
          email: userData.data.user.email,
          firstName: userData.data.user.firstName,
          lastName: userData.data.user.lastName,
          userType: 'user',
          mustChangePassword: userData.data.user.mustChangePassword || false,
          token: token  // Include token in userData
        };

        console.log('📦 Passing user data with token to onAuthSuccess:', userInfo);
        onAuthSuccess(userInfo);
      } else {
        const errorData = await userResponse.json();
        console.log('❌ User login failed:', errorData);
        setError(errorData.message || 'Invalid email or password.');
      }
    } catch (error) {
      console.error('Login error:', error);
      // Check if it's a network error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setError('Unable to connect to server. Please check your internet connection or try again later.');
      } else {
        setError('An unexpected error occurred. Please try again later.');
      }
    }

    setIsSubmitting(false);
  };

  return (
    <div className="card shadow-lg border-0" style={{ maxWidth: '450px', width: '100%' }}>
      <div className="card-body p-4 p-md-5">
        {/* ARCA Logo */}
        <div className="text-center mb-4">
          <img
            src={arcaLogo}
            alt="ARCA Logo"
            style={{ width: '180px', height: 'auto', marginBottom: '10px' }}
          />
        </div>

        <h2 className="text-center h4 fw-semibold text-dark mb-2">Sign in</h2>
        <p className="text-center text-muted mb-4">to continue to your account</p>

        {/* Error message display */}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Email input field */}
          <div className="mb-3">
            <label htmlFor="email" className="form-label fw-medium">Email Address</label>
            <input
              type="email"
              id="email"
              className="form-control form-control-lg"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
              aria-label="Email Address"
            />
          </div>

          {/* Password input field */}
          <div className="mb-4">
            <label htmlFor="password" className="form-label fw-medium">Password</label>
            <div className="position-relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                className="form-control form-control-lg"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Your password"
                aria-label="Password"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="btn btn-link position-absolute top-50 end-0 translate-middle-y"
                onClick={() => setShowPassword(!showPassword)}
                style={{ textDecoration: 'none', color: '#6B8E23' }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
              </button>
            </div>
          </div>

          {/* Sign In button */}
          <div className="d-grid mb-3">
            <button
              type="submit"
              className="btn btn-success btn-lg"
              style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </div>
        </form>

        {/* "Forgot password?" and "Create account" links */}
        <div className="text-center">
          <button
            onClick={onForgotPassword}
            className="btn btn-link text-success text-decoration-none me-3 p-0"
            aria-label="Forgot Password?"
          >
            Forgot Password?
          </button>
          <button
            onClick={onSwitchToMembership}
            className="btn btn-link text-success text-decoration-none p-0"
            aria-label="Create account"
          >
            Become a Member
          </button>
        </div>
      </div>
    </div>
  );
};

// Component for the Password Reset Form - Updated
const PasswordResetForm = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setMessageType('');
    setIsSubmitting(true);

    try {
      // Send password reset request to admin endpoint first - FIXED ENDPOINT
      const adminResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/admin/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      // Also send to regular user endpoint - FIXED ENDPOINT
      const userResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      // Always show success message to prevent email enumeration
      setMessage('If an account with that email exists, a password reset link has been sent.');
      setMessageType('success');
      setEmail(''); // Clear the email field
    } catch (error) {
      console.error('Password reset error:', error);
      // Check if it's a network error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setMessage('Unable to connect to server. Please check your internet connection or try again later.');
      } else {
        setMessage('An unexpected error occurred. Please try again later.');
      }
      setMessageType('error');
    }

    setIsSubmitting(false);
  };

  return (
    <div className="card shadow-lg border-0" style={{ maxWidth: '450px', width: '100%' }}>
      <div className="card-body p-4 p-md-5">
        {/* ARCA Logo */}
        <div className="text-center mb-4">
          <img
            src={arcaLogo}
            alt="ARCA Logo"
            style={{ width: '180px', height: 'auto', marginBottom: '10px' }}
          />
        </div>

        <h2 className="text-center h4 fw-semibold text-dark mb-2">Forgot your password?</h2>
        <p className="text-center text-muted mb-4 small">Enter your email address and we'll send you a link to reset your password.</p>

        {/* Message display for success or error */}
        {message && (
          <div className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="reset-email" className="form-label fw-medium">Email Address</label>
            <input
              type="email"
              id="reset-email"
              className="form-control form-control-lg"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
              aria-label="Email Address for password reset"
              autoComplete="email"
            />
          </div>

          <div className="d-grid mb-3">
            <button
              type="submit"
              className="btn btn-success btn-lg"
              style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Sending Link...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </div>
        </form>

        {/* Back to Sign In button */}
        <div className="text-center">
          <button
            onClick={onBackToLogin}
            className="btn btn-link text-primary text-decoration-none p-0"
            aria-label="Back to Sign In"
          >
            &larr; Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthForms; // Correctly exports the main AuthForms component